import React, { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Disclaimer } from "@/components/Common";
import { useAuth } from "@/lib/auth";
import { Plus, Search, Pencil, Globe2, Save } from "lucide-react";
import { toast } from "sonner";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";

const BLANK = {
  country_or_territory: "",
  society_code: "",
  society_name: "",
  rights_type_or_repertoire: "",
  active_status: true,
  notes: "",
  source_url: "",
  last_reviewed_date: "",
};

export default function PRODirectory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [editing, setEditing] = useState(null); // { ...entry } or BLANK with __new flag
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/pro-directory", { params: activeOnly ? { active_only: true } : {} });
      setItems(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeOnly]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter((s) => {
      const hay = [
        s.society_code, s.society_name, s.country_or_territory,
        s.rights_type_or_repertoire, s.notes,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [items, q]);

  function openNew() { setEditing({ ...BLANK, __new: true }); }
  function openEdit(entry) { setEditing({ ...entry }); }
  function closeEditor() { setEditing(null); }

  async function save() {
    if (!editing) return;
    if (!editing.society_code?.trim() || !editing.society_name?.trim()) {
      toast.error("Society code and society name are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        country_or_territory: editing.country_or_territory || "",
        society_code: editing.society_code.trim(),
        society_name: editing.society_name.trim(),
        rights_type_or_repertoire: editing.rights_type_or_repertoire || "",
        active_status: !!editing.active_status,
        notes: editing.notes || "",
        source_url: editing.source_url || "",
        last_reviewed_date: editing.last_reviewed_date || "",
      };
      if (editing.__new) {
        await api.post("/pro-directory", payload);
        toast.success("Directory entry created");
      } else {
        await api.put(`/pro-directory/${editing.pro_id}`, payload);
        toast.success("Directory entry updated");
      }
      closeEditor();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function remove(entry) {
    // legacy raw delete replaced by <InlineDeleteButton /> in the table row.
    // Kept for backwards compatibility with any external callers.
    try {
      await api.delete(`/pro-directory/${entry.pro_id}?reason=Test%20record&force=true`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <div data-testid="pro-directory-page">
      <PageHeader
        title="PRO / CMO Reference Directory"
        subtitle="Admin-managed lookup of performing-rights / collective management organisations. Track-level PRO selection pulls from this list."
        actions={isAdmin ? (
          <Button onClick={openNew} data-testid="pro-add-button" style={{ background: "hsl(347 72% 22%)" }}>
            <Plus className="w-4 h-4 mr-2" />Add entry
          </Button>
        ) : null}
      />

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs text-muted-foreground">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Society code, name, country, repertoire…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
                data-testid="pro-search-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={activeOnly} onCheckedChange={setActiveOnly} data-testid="pro-active-only-switch" />
            <Label className="text-xs">Active only</Label>
          </div>
          <span className="text-xs text-muted-foreground self-end" data-testid="pro-count">
            {filtered.length} of {items.length} entries
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Code</TableHead>
                <TableHead>Society / Organisation</TableHead>
                <TableHead>Country / Territory</TableHead>
                <TableHead>Repertoire / Rights</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Last reviewed</TableHead>
                <TableHead className="w-[140px] text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                  <Globe2 className="w-6 h-6 mx-auto mb-2 opacity-60" />
                  No directory entries match. {isAdmin ? "Add one to seed your reference list." : ""}
                </TableCell></TableRow>
              )}
              {filtered.map((s) => (
                <TableRow key={s.pro_id} data-testid={`pro-row-${s.pro_id}`}>
                  <TableCell className="font-mono text-xs">{s.society_code}</TableCell>
                  <TableCell className="font-medium">{s.society_name}</TableCell>
                  <TableCell className="text-sm">{s.country_or_territory || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">{s.rights_type_or_repertoire || "—"}</TableCell>
                  <TableCell>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${s.active_status ? "status-client" : "status-incomplete"}`}>
                      {s.active_status ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{(s.last_reviewed_date || "").slice(0, 10) || "—"}</TableCell>
                  <TableCell className="text-right">
                    {isAdmin ? (
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`pro-edit-${s.pro_id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <InlineDeleteButton
                          endpoint={`/pro-directory/${s.pro_id}`}
                          recordLabel="PRO entry"
                          recordId={s.pro_id}
                          isSuperAdmin={isAdmin}
                          variant="ghost"
                          onDeleted={() => load()}
                          testid={`pro-delete-${s.pro_id}`}
                        />
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">Read-only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Disclaimer>
        Directory entries are internal reference data only · This list is editable for future expansion and is not exhaustive of all global societies.
      </Disclaimer>

      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.__new ? "Add directory entry" : "Edit directory entry"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Society code" required>
                <Input
                  value={editing.society_code}
                  onChange={(e) => setEditing({ ...editing, society_code: e.target.value })}
                  placeholder="e.g. ASCAP"
                  data-testid="pro-edit-code"
                />
              </Field>
              <Field label="Country / Territory">
                <Input
                  value={editing.country_or_territory}
                  onChange={(e) => setEditing({ ...editing, country_or_territory: e.target.value })}
                  placeholder="e.g. United States"
                  data-testid="pro-edit-country"
                />
              </Field>
              <Field full label="Society / Organisation name" required>
                <Input
                  value={editing.society_name}
                  onChange={(e) => setEditing({ ...editing, society_name: e.target.value })}
                  placeholder="Full legal name"
                  data-testid="pro-edit-name"
                />
              </Field>
              <Field full label="Rights type / Repertoire">
                <Input
                  value={editing.rights_type_or_repertoire}
                  onChange={(e) => setEditing({ ...editing, rights_type_or_repertoire: e.target.value })}
                  placeholder="e.g. Performing rights — musical works"
                  data-testid="pro-edit-rights"
                />
              </Field>
              <Field label="Source URL">
                <Input
                  value={editing.source_url}
                  onChange={(e) => setEditing({ ...editing, source_url: e.target.value })}
                  placeholder="https://…"
                  data-testid="pro-edit-source"
                />
              </Field>
              <Field label="Last reviewed date">
                <Input
                  type="date"
                  value={(editing.last_reviewed_date || "").slice(0, 10)}
                  onChange={(e) => setEditing({ ...editing, last_reviewed_date: e.target.value })}
                  data-testid="pro-edit-reviewed"
                />
              </Field>
              <Field full label="Notes">
                <Textarea
                  rows={3}
                  value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  data-testid="pro-edit-notes"
                />
              </Field>
              <Field label="Active status">
                <div className="flex items-center gap-2 h-9">
                  <Switch
                    checked={!!editing.active_status}
                    onCheckedChange={(v) => setEditing({ ...editing, active_status: v })}
                    data-testid="pro-edit-active"
                  />
                  <span className="text-xs text-muted-foreground">{editing.active_status ? "Active" : "Inactive"}</span>
                </div>
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEditor} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="pro-edit-save" style={{ background: "hsl(347 72% 22%)" }}>
              <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs">{label} {required && <span style={{ color: "hsl(347 72% 30%)" }}>*</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
